// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { type OptionsObject, useSnackbar } from 'notistack';
import { useCallback } from 'react';
import { proposalsApi } from '@shared/data-access/api/brokerage/proposals-api';
import { useTranslation } from 'react-i18next';
import { type InternalProposalOperation } from '@shared/data-access/interfaces/api/brokerage/proposals-types';
import type { ProposalScopedInstitutionAccounts } from '@shared/data-access/transformers/proposals/build-proposal-payload';

export interface UseSentProposalArgs {
  readonly clientId: number;
  readonly riskProfile: string | null;
  readonly name: string;
  readonly description: string;
  readonly operations: InternalProposalOperation[];
  readonly currency: string;
  readonly advisoryProposalAccounts: readonly ProposalScopedInstitutionAccounts[];
}

const commonSnackProps: OptionsObject = {
  anchorOrigin: { horizontal: 'right', vertical: 'bottom' },
};

export const useSentProposal = () => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();

  const [sendProposal, sendProposalQuery] = proposalsApi.useCreateProposalMutation({
    fixedCacheKey: 'shared-aggregator-create-proposal',
  });

  const onSendProposal = useCallback(
    async (args: UseSentProposalArgs) => {
      try {
        await sendProposal({
          client_id: args.clientId,
          name: args.name,
          description: args.description,
          operations: args.operations,
          currency: args.currency,
          risk_profile: args.riskProfile,
          advisory_proposal_accounts: args.advisoryProposalAccounts,
        }).unwrap();

        enqueueSnackbar(
          t('proposal-modal.proposal-sent-success', { ns: 'components' }),
          {
            ...commonSnackProps,
            variant: 'success',
          },
        );
      } catch (error) {
        console.error('Error sending proposal:', error);

        enqueueSnackbar(
          t('proposal-modal.proposal-sent-error', {
            ns: 'components',
          }),
          {
            ...commonSnackProps,
            variant: 'error',
          },
        );
      }
    },
    [enqueueSnackbar, sendProposal, t],
  );

  return {
    ...sendProposalQuery,
    onSendProposal,
  } as const;
};
